<template>
  <div class="v-application">
    <div style="width: 100%" v-if="dataInitiallyLoading">
      <NewListItemLoadingSkeleton
        v-for="i in 3"
        :key="i"
        style="background: white"
      />
    </div>
    <ErrorAlert
      title="Fehler beim Laden"
      message="Beim Laden der Einladungen ist ein Fehler passiert. Versuche es später erneut."
      v-else-if="error"
    ></ErrorAlert>
    <template v-else>
      <i style="color: rgba(0, 0, 0, 0.5)" v-if="availableEvents.length === 0">
        Du hast keine unbeantworteten Einladungen zu Veranstaltungen.
      </i>
      <v-list v-else style="width: 100%; padding: 0">
        <v-scroll-x-transition group>
          <template v-for="event in availableEvents">
            <RSVPDashboardListItem
              :key="event.id"
              @click="selectEvent(event.id)"
              @rsvpExpired="handleRsvpExpired(event.id, $event)"
              :expanded="isSelected(event.id)"
              :event="event"
            />
          </template>
        </v-scroll-x-transition>
      </v-list>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, Ref, ref, watch } from "vue"
import NewListItem from "components/NewListItem.vue"
import AccessIcon from "features/common/ui/AccessIcon.vue"
import Button from "components/UI/Button.vue"
import AttendWithGuestsDialog from "app/modules/events/rsvp-dashboard/AttendWithGuestsDialog.vue"
import RSVPDashboardListItem from "app/modules/events/rsvp-dashboard/RSVPDashboardListItem.vue"
import { eventsApi, rsvpApi } from "store/api"
import ErrorAlert from "app/ui-primitives/alerts/ErrorAlert.vue"
import NewListItemLoadingSkeleton from "components/NewListItemLoadingSkeleton.vue"
import useCurrentMember from "concerns/useCurrentMember"
import { CurrentMember, RsvpRequest } from "suus-api"

export default defineComponent({
  name: "RSVPDashboardList",
  components: {
    NewListItemLoadingSkeleton,
    ErrorAlert,
    RSVPDashboardListItem,
    AttendWithGuestsDialog,
    Button,
    AccessIcon,
    NewListItem,
  },
  setup() {
    const dataInitiallyLoading = ref(true)
    const error = ref(null)
    const availableEvents = ref([])
    const { currentMemberPromise } = useCurrentMember()
    let currentMember: Ref<CurrentMember | null> = undefined

    // Semaphore to prevent concurrent declines with following RSVP reloads
    //  which cause double reloads. Only if no other requests are ongoing,
    //  an RSVP reload is made eventually.
    const requestOngoings = ref(0)
    watch(requestOngoings, (value) => {
      console.log("Request ongoings", value)
    })

    const fetchOpenRSVPEvents = async (member: CurrentMember) => {
      error.value = null
      const personalRecordId = member?.personal_record?.id

      try {
        requestOngoings.value++
        const response = await eventsApi.getEvents(
          undefined,
          undefined,
          personalRecordId,
          undefined,
          undefined,
          undefined,
          new Date().toISOString(),
          undefined,
          undefined,
          3
        )
        if (response.status === 204) {
          availableEvents.value = []
        } else if (response.status === 200) {
          // Only if no other requests (except from this reply + reload cycle) have been started while reloading, update the list.
          // Otherwise the reload after the final response will be sufficient.
          console.log(
            "Data received, sempaphore is at " + requestOngoings.value
          )
          if (requestOngoings.value === 2) {
            availableEvents.value = response.data
            selectedEventId.value ||= availableEvents.value[0]?.id
          }
        }
      } catch (e) {
        console.error("Error fetching open RSVP events", e)
        error.value = e
      } finally {
        dataInitiallyLoading.value = false
        requestOngoings.value--
      }
    }

    currentMemberPromise.then(async (member) => {
      requestOngoings.value++
      currentMember = member
      await fetchOpenRSVPEvents(currentMember.value)
      requestOngoings.value--
    })

    const selectedEventId = ref<number | null>(null)
    const selectEvent = (eventId: number) => {
      selectedEventId.value = selectedEventId.value === eventId ? null : eventId
    }
    const isSelected = (eventId: number) => selectedEventId.value === eventId

    const handleRsvpExpired = async (
      eventId: number,
      reply_data: RsvpRequest
    ) => {
      availableEvents.value = availableEvents.value.filter(
        (event) => event.id !== eventId
      )

      if (selectedEventId.value === eventId) {
        selectedEventId.value = availableEvents.value[0]?.id
      }

      try {
        requestOngoings.value++
        await rsvpApi.updateRsvp(
          eventId.toString(),
          currentMember.value?.personal_record?.id.toString(),
          reply_data
        )

        // If no other requests are ongoing, reload RSVP. Since fetchOpenRSVPEvents
        // internally also maintains the request counter, this means that only at
        // the end of all rsvp requests the list is reloaded
        if (requestOngoings.value === 1) {
          await fetchOpenRSVPEvents(currentMember.value)
        }
      } catch (e) {
        console.error("Error updating RSVP", e)
      } finally {
        requestOngoings.value--
      }
    }

    return {
      selectEvent,
      isSelected,
      availableEvents,
      handleRsvpExpired,
      dataInitiallyLoading,
      error,
    }
  },
})
</script>
<style scoped>
.scroll-x-transition-move {
  transition: none;
}
</style>
